import { SectionEmptyState } from '../../components/ui/States';
import { authService } from '../../services/authService';
import { useBillingSummary } from './BillingSummaryProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AiCreditPurchaseSummary,
  CreditTransactionHistoryEntry,
  CREDIT_PACKS,
  parseCreditTransactionHistoryResult,
  parseCreateAiCreditCheckoutResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import {
  createAiCreditCheckout,
  listCreditTransactions,
} from '../../firebase/functions';
import {
  billingAuthenticationMessage,
  ReauthenticationCancelled,
  useBillingReauthentication,
} from './useBillingReauthentication';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ListIcon,
} from '../../components/ui/icons';
import { useNavigation } from '../navigation/NavigationProvider';
import { appDestination, parseAppRoute } from '../app/appRoutes';
import { surfaceShadows } from '../../surfaceStyles';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';

const dateLabel = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
const billingTimeLabel = (value: string): string =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

const transactionLabels = {
  grant: 'Credit grant',
  'ai-refund': 'AI refund',
  reversal: 'Payment reversal',
  adjustment: 'Balance adjustment',
  'monthly-item-charge': 'Monthly item billing',
  'ai-charge': 'AI keyword generation',
};

const money = (amountMinor: number): string =>
  new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
  }).format(amountMinor / 100);

export function BillingSection({
  userId,
  purchaseReturn,
  purchaseDialog = false,
}: {
  userId?: string;
  purchaseReturn?: 'returned' | 'cancelled';
  purchaseDialog?: boolean;
}) {
  const navigation = useNavigation();
  const route = parseAppRoute(navigation.location.pathname);
  const view =
    route.surface === 'buy-credits'
      ? 'buy'
      : route.surface === 'transactions'
        ? 'transactions'
        : 'overview';
  const navigate = (surface: 'buy-credits' | 'transactions') =>
    navigation.navigate(appDestination(route.tab, surface));
  const reauthentication = useBillingReauthentication();
  const purchaseOperations = useRef<Record<string, string>>({});
  const purchaseInFlight = useRef(false);
  useEffect(() => {
    purchaseOperations.current = {};
    if (!userId || !purchaseReturn) return;
    // A return permits a deliberate new purchase; it never changes the balance.
    for (const pack of Object.keys(CREDIT_PACKS)) {
      try {
        window.sessionStorage.removeItem(
          `qkindex:credit-checkout:${userId}:${pack}`,
        );
      } catch {
        /* Storage is optional. */
      }
    }
  }, [userId, purchaseReturn]);
  const accountId = userId || authService.currentUser()?.uid || '';
  const { store, snapshot } = useBillingSummary(accountId);
  const { wallet: summary, billing } = snapshot;
  const refreshFailed = Boolean(snapshot.error);
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const [transactions, setTransactions] = useState<
    CreditTransactionHistoryEntry[]
  >([]);
  const [transactionCursor, setTransactionCursor] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErrors, setHistoryErrors] = useState<string>();
  const historyRequests = useRef<object | undefined>(undefined);
  const historyStarted = useRef(false);
  const [buying, setBuying] = useState<string>();
  const [error, setError] = useState<string>();
  const [selectedPack, setSelectedPack] = useState<string>();
  const loadGeneration = useRef<object>({});
  const purchasingAllowed =
    summary?.purchasingAvailable === true &&
    summary.reconciliationRequired !== true &&
    billing?.reconciliationPending !== true;
  const pack =
    summary?.packs.find((entry) => entry.packKey === selectedPack) ||
    summary?.packs[0];

  const loadHistory = useCallback(
    async (cursor?: string) => {
      if (historyRequests.current) return;
      historyStarted.current = true;
      const generation = loadGeneration.current;
      const request = {};
      historyRequests.current = request;
      const current = () =>
        accountRef.current === accountId &&
        loadGeneration.current === generation &&
        historyRequests.current === request;
      setHistoryLoading(true);
      setHistoryErrors(undefined);
      try {
        const result = parseCreditTransactionHistoryResult(
          (
            await listCreditTransactions({
              pageSize: 10,
              ...(cursor ? { cursor } : {}),
            })
          ).data,
        );
        if (!current()) return;
        setTransactions((items) =>
          cursor ? [...items, ...result.items] : [...result.items],
        );
        setTransactionCursor(result.nextCursor);
      } catch (failure) {
        if (current())
          setHistoryErrors(
            billingAuthenticationMessage(failure) ||
              'Transactions are temporarily unavailable.',
          );
      } finally {
        if (current()) {
          historyRequests.current = undefined;
          setHistoryLoading(false);
        }
      }
    },
    [accountId],
  );

  const load = useCallback(
    async (force = false) => {
      const generation = {};
      loadGeneration.current = generation;
      historyRequests.current = undefined;
      historyStarted.current = false;
      const current = () =>
        accountRef.current === accountId &&
        loadGeneration.current === generation;
      setLoading(true);
      setHistoryLoading(false);
      setHistoryErrors(undefined);
      setError(undefined);
      setTransactions([]);
      setTransactionCursor(undefined);
      try {
        const cached = store.getSnapshot();
        const hasSummary =
          cached.userId === accountId && cached.billing && cached.wallet;
        // Reuse the session summary immediately. Initialize a missing account before history reads.
        const refresh = store.refresh(accountId, force);
        const result = hasSummary && !force ? cached : await refresh;
        if (!current() || !result || !result.wallet || !result.billing) return;
        if (view === 'transactions') {
          void loadHistory();
        }
      } catch (failure) {
        if (current())
          setError(
            billingAuthenticationMessage(failure) ||
              'Billing information is temporarily unavailable.',
          );
      } finally {
        if (current()) setLoading(false);
      }
    },
    [store, accountId, view, loadHistory],
  );

  useEffect(() => {
    void load();
    return () => {
      loadGeneration.current = {};
      historyRequests.current = undefined;
    };
  }, [load]);

  useEffect(() => {
    // A session refresh can recover the summary after the initial load failed.
    // Start history once per visit; later refreshes preserve pages and retry state.
    if (
      view === 'transactions' &&
      summary &&
      billing &&
      !loading &&
      !historyStarted.current
    ) {
      void loadHistory();
    }
  }, [view, summary, billing, loading, loadHistory]);

  const buy = async (packKey: AiCreditPurchaseSummary['packKey']) => {
    if (purchaseInFlight.current || !purchasingAllowed) return;
    const generation = loadGeneration.current;
    const current = () =>
      accountRef.current === accountId && loadGeneration.current === generation;
    purchaseInFlight.current = true;
    setBuying(packKey);
    setError(undefined);
    try {
      const storageKey = userId
        ? `qkindex:credit-checkout:${userId}:${packKey}`
        : undefined;
      let operationId = purchaseOperations.current[packKey];
      if (!operationId && storageKey) {
        try {
          operationId = window.sessionStorage.getItem(storageKey) || '';
        } catch {
          /* Storage is optional. */
        }
      }
      if (!operationId) operationId = createOperationId();
      purchaseOperations.current[packKey] = operationId;
      if (storageKey) {
        try {
          window.sessionStorage.setItem(storageKey, operationId);
        } catch {
          /* Keep the in-memory retry identity. */
        }
      }
      const request = { operationId, packKey };
      const result = parseCreateAiCreditCheckoutResult(
        (await reauthentication.run(() => createAiCreditCheckout(request)))
          .data,
      );
      if (!current()) return;
      window.location.assign(result.hostedUrl);
    } catch (failure) {
      if (current() && !(failure instanceof ReauthenticationCancelled))
        setError(
          billingAuthenticationMessage(failure) ||
            'Checkout could not be opened. Retry the same pack to recover this purchase, or sign in again.',
        );
    } finally {
      if (current()) setBuying(undefined);
      purchaseInFlight.current = false;
    }
  };

  const purchaseAction = purchasingAllowed && (
    <Button
      variant="contained"
      fullWidth
      disabled={Boolean(buying) || !pack}
      onClick={() => {
        if (pack) void buy(pack.packKey);
      }}
    >
      {buying ? 'Opening…' : 'Continue'}
    </Button>
  );

  const content = (
    <Stack
      spacing={3}
      sx={{
        width: '100%',
        maxWidth: view === 'buy' ? 'none' : 480,
        mx: 'auto',
        py: 1,
      }}
    >
      {reauthentication.dialog}
      {loading && !summary && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress
            size={24}
            aria-label="Loading billing information"
          />
        </Box>
      )}
      {refreshFailed && (
        <Alert
          severity="warning"
          action={
            <Button
              onClick={() => {
                void load(true);
              }}
              disabled={loading || snapshot.loading || Boolean(buying)}
            >
              {snapshot.loading ? 'Trying…' : 'Retry'}
            </Button>
          }
        >
          {snapshot.loading
            ? 'Updating your balance…'
            : billingAuthenticationMessage(snapshot.error) ||
              (summary
                ? 'Couldn’t update your balance.'
                : 'Billing information is temporarily unavailable.')}
        </Alert>
      )}
      {error && <Alert severity="warning">{error}</Alert>}
      {summary && (
        <>
          <Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  ...(view === 'buy' && {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    width: '100%',
                  }),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {summary.balance < 0
                    ? 'Credit balance'
                    : view === 'buy'
                      ? 'Available credits'
                      : 'Credits available'}
                </Typography>
                <Typography
                  variant={view === 'buy' ? 'h6' : 'h4'}
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {summary.balance.toLocaleString()}
                </Typography>
                {view === 'overview' && billing && billing.freeAiUses > 0 && (
                  <Typography
                    variant="body2"
                    color="primary.main"
                    sx={{ mt: 1 }}
                  >
                    {billing.freeAiUses.toLocaleString()} free AI uses remaining
                  </Typography>
                )}
                {summary.balance < 0 && view !== 'buy' && (
                  <Typography variant="body2" color="warning.main">
                    {Math.abs(summary.balance).toLocaleString()} credits owed
                  </Typography>
                )}
              </Box>
              {view === 'overview' && (
                <Button
                  variant="contained"
                  aria-label="Buy credits"
                  onClick={() => navigate('buy-credits')}
                  sx={{
                    flexShrink: 0,
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    p: 0,
                  }}
                >
                  <AddIcon />
                </Button>
              )}
            </Stack>
            {refreshFailed && (
              <Typography
                variant="caption"
                component="p"
                color="warning.main"
                role="status"
                sx={{ mt: 1 }}
              >
                May be out of date
              </Typography>
            )}
          </Box>
          {billing?.reconciliationPending && (
            <Alert severity="warning">
              Billing changes are paused while account state is reconciled.
            </Alert>
          )}
          {summary.reconciliationRequired && (
            <Alert severity="warning">
              Purchases and AI use are paused while this account is reviewed.
            </Alert>
          )}
          {billing?.itemBilling.restricted && (
            <Alert severity="warning">
              Adding and editing items are paused until your balance reaches
              zero or higher. You can still view, export, delete items, manage
              access and purchase credits. Monthly charges continue while
              billable items remain.
            </Alert>
          )}
          {view === 'overview' && (
            <>
              {billing && (
                <Card
                  component="section"
                  aria-label="Monthly item billing"
                  sx={{ p: { xs: 2, sm: 2.5 } }}
                >
                  <Typography variant="subtitle1" component="h3">
                    Monthly item billing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dateLabel(billing.itemBilling.periodStart)} –{' '}
                    {dateLabel(billing.itemBilling.periodEnd)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                      gap: 2,
                      py: 2.5,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current items
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {billing.itemBilling.currentItems.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Peak items this month
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {billing.itemBilling.peakItems.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                      gap: 1.5,
                      pt: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Next billing date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dateLabel(billing.itemBilling.periodEnd)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {billingTimeLabel(billing.itemBilling.periodEnd)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">
                        Est. deduction
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {billing.itemBilling.projectedCredits.toLocaleString()}{' '}
                        credits
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.09),
                    }}
                  >
                    <Typography variant="body2" color="primary.main">
                      {billing.itemBilling.billableItems === 0
                        ? `Within your ${billing.itemBilling.freeItems.toLocaleString()} free items`
                        : `${billing.itemBilling.billableItems.toLocaleString()} billable peak items · ${billing.itemBilling.freeItems.toLocaleString()} items free`}
                    </Typography>
                  </Box>
                  <Box
                    component="details"
                    sx={{ mt: 1.5, color: 'text.secondary', '& p': { mt: 1 } }}
                  >
                    <Box
                      component="summary"
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        minHeight: 44,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&::-webkit-details-marker': { display: 'none' },
                      }}
                    >
                      <ChevronDownIcon />
                      <Typography variant="body2" component="span">
                        How billing works
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Your first{' '}
                      {billing.itemBilling.freeItems.toLocaleString()} items in
                      the collections you own are free. Additional items cost 1
                      credit per 20 items.
                    </Typography>
                    <Typography variant="body2">
                      Items in your Bin still count to your total items. Billing
                      runs on a monthly basis, based on when your account was
                      created.
                    </Typography>
                  </Box>
                </Card>
              )}
              <Button
                startIcon={<ListIcon />}
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('transactions')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  borderRadius: 0,
                  '& .MuiButton-endIcon': { ml: 'auto' },
                }}
              >
                Transactions
              </Button>
            </>
          )}
          {view === 'buy' && (
            <Stack spacing={2}>
              {summary.balance < 0 && (
                <Alert severity="info">
                  Your purchase first clears{' '}
                  {Math.abs(summary.balance).toLocaleString()} credits owed.
                </Alert>
              )}
              {!summary.purchasingAvailable &&
                !summary.reconciliationRequired && (
                  <Alert severity="info">
                    Credit purchasing is not available yet.
                  </Alert>
                )}
              {purchasingAllowed && (
                <>
                  <RadioGroup
                    aria-label="Credit pack"
                    value={pack?.packKey || ''}
                    onChange={(event) => setSelectedPack(event.target.value)}
                    sx={{ gap: 2 }}
                  >
                    {summary.packs.map((option) => (
                      <FormControlLabel
                        key={option.packKey}
                        value={option.packKey}
                        disabled={Boolean(buying)}
                        control={<Radio />}
                        label={
                          <Stack
                            direction="row"
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Typography>
                              {option.credits.toLocaleString()} credits
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {money(option.amountMinor)}
                            </Typography>
                          </Stack>
                        }
                        sx={{
                          m: 0,
                          p: 1.5,
                          minHeight: 80,
                          border: 0,
                          boxShadow:
                            option.packKey === pack?.packKey
                              ? surfaceShadows.inset
                              : surfaceShadows.raised,
                          borderRadius: 1,
                          bgcolor: (theme) =>
                            option.packKey === pack?.packKey
                              ? alpha(theme.palette.primary.main, 0.09)
                              : 'background.paper',
                          '& .MuiFormControlLabel-label': { flex: 1, pr: 1 },
                        }}
                      />
                    ))}
                  </RadioGroup>
                  {!purchaseDialog && purchaseAction}
                </>
              )}
            </Stack>
          )}
          {view === 'transactions' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Purchases, monthly billing and AI usage
              </Typography>
              {historyErrors && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      onClick={() => {
                        void loadHistory(transactionCursor);
                      }}
                      disabled={historyLoading}
                    >
                      Retry transactions
                    </Button>
                  }
                >
                  {historyErrors}
                </Alert>
              )}
              {historyLoading && (
                <CircularProgress size={24} aria-label="Loading transactions" />
              )}
              {!transactions.length && !historyLoading && !historyErrors && (
                <SectionEmptyState
                  compact
                  icon={ListIcon}
                  message="No credit transactions yet."
                />
              )}
              {transactions.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2">
                      {entry.reason === 'ai-charge'
                        ? 'AI keyword generation'
                        : entry.reason === 'grant' && entry.purchaseId
                          ? 'Credit purchase'
                          : transactionLabels[entry.reason]}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        textAlign: 'right',
                      }}
                    >
                      {entry.amount > 0 ? '+' : ''}
                      {entry.amount.toLocaleString()} credits
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {entry.reason === 'ai-charge'
                      ? `${new Date(entry.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })} · ${entry.chargeCount.toLocaleString()} ${entry.chargeCount === 1 ? 'charge' : 'charges'} · UTC`
                      : dateLabel(entry.createdAt)}
                  </Typography>
                </Box>
              ))}
              {transactionCursor && (
                <Button
                  disabled={historyLoading}
                  onClick={() => {
                    void loadHistory(transactionCursor);
                  }}
                >
                  Load more transactions
                </Button>
              )}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );

  return purchaseDialog && view === 'buy' ? (
    <>
      <DialogContent>{content}</DialogContent>
      {purchaseAction && (
        <DialogActions compact sx={{ '& > *': { flex: 1 } }}>
          {purchaseAction}
        </DialogActions>
      )}
    </>
  ) : (
    content
  );
}
